import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router } from '@angular/router';

import { AccountStore } from './account-store';
import { ClubBrandStore } from './club-brand';
import { ClubJoin } from './club-join';

/** Whether the account already belongs to the club - `clubIds` is the user
 *  fan-in; `manages` covers a payer whose only tie is the people they pay
 *  for. The auto-join below asks this before writing anything. */
export function belongsToClub(store: AccountStore, clubId: string): boolean {
  const account = store.account();
  return (
    !!account &&
    (account.clubIds.includes(clubId) ||
      account.manages.some((managed) => managed.clubId === clubId))
  );
}

/**
 * The onboarding gate, app-local (it reads the member's account doc, which
 * is this app's data layer - the kit's guards stay auth-only). Runs AFTER
 * the kit's `verifiedGuard` in the /tabs guard chain, and does two jobs:
 *
 * **Auto-join**: every build of this app is a CLUB'S OWN app, so being
 *    signed in on it IS wanting to be in the club - there is no join gate,
 *    no code entry, no refusal door. An account that is not yet a user of
 *    the club (an existing Huddle login from another club, or a session
 *    from before this club's app shipped) is joined right here, under the
 *    boot veil, via the idempotent `joinClub` Callable. See
 *    docs/club-auth.md.
 *
 * A join that fails on transport is NOT a verdict on the person: the guard
 * lets them through and the next navigation retries. The rules deny club
 * data to a non-user either way, so the failure surface is empty page
 * states, never somebody else's data.
 *
 * Only ever returns `true` or a redirect UrlTree.
 */
export const onboardedGuard: CanActivateFn & CanActivateChildFn = async () => {
  const store = inject(AccountStore);
  const brand = inject(ClubBrandStore).brand();
  const clubJoin = inject(ClubJoin);
  const router = inject(Router);

  await store.whenLoaded();

  // NOT gated on a complete profile any more: the profile ask is a nudge
  // Landing makes on the way in, not a wall (see AccountStore.
  // shouldAskProfile). The join below simply fails for a half-named
  // account - `joinClub` requires an onboarded one server-side - and the
  // next navigation retries it once they finish, which is the same
  // best-effort contract a transport failure already had.
  //
  // The profile page joins on the way through; this covers every OTHER way
  // an onboarded account arrives - an existing Huddle login's first sign-in
  // to this club's app being the big one. (Brandless dev builds skip it.)
  if (brand && !belongsToClub(store, brand.clubId)) {
    try {
      await clubJoin.join(brand.clubId);
    } catch {
      // Carry on - see the note above. The account snapshot catches up on
      // its own; the next guard run retries the join if it never landed.
    }
  }
  return true;
};

/**
 * The profile route's own loader gate: waits for the account snapshot so
 * the page can decide synchronously what is left to collect (a hard
 * refresh on /complete-your-profile constructs the page before the
 * snapshot lands otherwise). Never redirects - it only holds activation.
 */
export const accountLoadedGuard: CanActivateFn = async () => {
  const store = inject(AccountStore);
  await store.whenLoaded();
  return true;
};
